import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, Plus, X, Sparkles, HelpCircle, Check, Shield, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import MultiSelect from "@/components/MultiSelect";
import { COUNTRIES, SECTORS, TR_CITIES, TR_DISTRICTS, TARGET_AUDIENCES, sectorLabel, countryLabel, audienceLabel } from "@/data/locations";
import { useLang } from "@/contexts/LanguageContext";

const SERVICES_PLACEHOLDER = {
  restaurant: "Örn: kahvaltı, İtalyan mutfağı, vegan seçenekler, aile restoranı, rezervasyon",
  dental: "Örn: implant, diş beyazlatma, ortodonti, çocuk diş hekimliği, acil diş tedavisi",
  beauty: "Örn: lazer epilasyon, cilt bakımı, bölgesel incelme, kaş tasarımı, medikal cilt bakımı",
  hotel: "Örn: oda türleri, kahvaltı, otopark, aile odası, iş seyahati, merkeze yakınlık",
  camp: "Örn: çadır kampı, karavan alanı, duş, elektrik, denize yakınlık, aile uygunluğu",
  auto: "Örn: periyodik bakım, BMW özel servis, lastik değişimi, kaporta, mekanik bakım, acil servis",
  school: "Örn: anaokulu, İngilizce kursu, LGS hazırlık, özel ders, öğretmen kadrosu",
  realestate: "Örn: satılık daire, kiralık daire, bölge uzmanlığı, yatırım danışmanlığı, ekspertiz",
  general: "Örn: sunduğunuz ana hizmetleri virgülle yazın",
};

// Build the legacy `city` string that the backend (and analysis prompts) expect.
// We keep "Şehir / İlçe" Turkish convention so the LocalBusiness Schema builder
// continues to map City→addressRegion and District→addressLocality correctly.
function composeCityField({ country, city, district }) {
  const parts = [];
  if (city) parts.push(city.trim());
  if (district) parts.push(district.trim());
  let out = parts.join(" / ");
  if (country && country !== "Türkiye" && country !== "") {
    // Suffix non-Turkish country so the audit prompts know the locale.
    out = out ? `${out}, ${country}` : country;
  }
  return out;
}

// Build the `target_customer` string from a tag list + optional free-text "Diğer" note.
function composeAudience(tags, otherText) {
  const cleaned = (tags || []).filter((t) => t && t !== "Diğer");
  const result = cleaned.join(", ");
  const other = (otherText || "").trim();
  if ((tags || []).includes("Diğer") && other) {
    return result ? `${result}, ${other}` : other;
  }
  return result;
}

export default function Onboarding() {
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "", sector: "", website_url: "", google_maps_url: "",
    description: "", services: "", target_ai_questions: "",
  });
  const [country, setCountry] = useState("Türkiye");
  const [city, setCity] = useState("");
  const [district, setDistrict] = useState("");
  const [audience, setAudience] = useState([]);
  const [audienceOther, setAudienceOther] = useState("");
  const [competitors, setCompetitors] = useState([""]);
  const [preview, setPreview] = useState(null);

  const districtOptions = useMemo(
    () => (country === "Türkiye" && TR_DISTRICTS[city]) || null,
    [country, city]
  );

  // Fetch sector preview when sector + city changes
  useEffect(() => {
    if (!form.sector) {
      setPreview(null);
      return;
    }
    let cancel = false;
    const t = setTimeout(() => {
      api.get(`/sectors/preview?sector=${encodeURIComponent(form.sector)}`)
        .then((r) => {
          if (cancel) return;
          const cityLabel = city || "şehrinizde";
          const questions = r.data.ai_questions.map((q) =>
            q.replace("{şehir}", cityLabel).replace("{sektör}", form.sector || "işletme")
          );
          setPreview({ ...r.data, ai_questions: questions });
        })
        .catch(() => setPreview(null));
    }, 200);
    return () => { cancel = true; clearTimeout(t); };
  }, [form.sector, city]);

  const change = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    const cityComposed = composeCityField({ country, city, district });
    if (!form.name || !form.sector || !cityComposed) {
      toast.error(t("onb.requiredErr"));
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/businesses", {
        ...form,
        city: cityComposed,
        target_customer: composeAudience(audience, audienceOther),
        competitors: competitors.map((c) => c.trim()).filter(Boolean),
      });
      toast.success(t("onb.savedToast"));
      navigate(`/app/isletme/${data.id}/analiz`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("onb.saveError"));
    } finally {
      setLoading(false);
    }
  };

  const isTR = country === "Türkiye";

  return (
    <div className="space-y-8" data-testid="onboarding-page">
      <div>
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-cyan-400 mb-2">
          <Sparkles size={12} /> {t("onb.eyebrow")}
        </div>
        <h1 className="font-display text-4xl font-bold text-white">{t("onb.title")}</h1>
        <p className="text-slate-400 mt-2 max-w-2xl">{t("onb.subtitle")}</p>
      </div>

      <form onSubmit={submit} className="glass-strong rounded-3xl p-8 space-y-6">
        <div className="grid md:grid-cols-2 gap-5">
          <Field label={t("onb.name")}>
            <input className="ueinput" required value={form.name} onChange={(e) => change("name", e.target.value)} data-testid="onb-name" />
          </Field>
          <Field label={t("onb.sector")}>
            <select className="ueinput" required value={form.sector} onChange={(e) => change("sector", e.target.value)} data-testid="onb-sector">
              <option value="">{t("onb.select")}</option>
              {SECTORS.map((s) => <option key={s.value} value={s.value}>{sectorLabel(s.value, lang)}</option>)}
            </select>
          </Field>
        </div>

        {/* Location block */}
        <div>
          <div className="text-xs text-slate-400 uppercase tracking-wider mb-2">{t("onb.location")}</div>
          <div className="grid md:grid-cols-3 gap-3">
            <select
              className="ueinput"
              value={country}
              onChange={(e) => {
                setCountry(e.target.value);
                setCity("");
                setDistrict("");
              }}
              data-testid="onb-country"
            >
              {COUNTRIES.map((c) => <option key={c.code} value={c.label}>{countryLabel(c, lang)}</option>)}
            </select>

            {isTR ? (
              <select
                className="ueinput"
                value={city}
                onChange={(e) => { setCity(e.target.value); setDistrict(""); }}
                data-testid="onb-city-select"
              >
                <option value="">{t("onb.cityPick")}</option>
                {TR_CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            ) : (
              <input
                className="ueinput"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder={t("onb.cityPh")}
                data-testid="onb-city-input"
              />
            )}

            {isTR && districtOptions ? (
              <select
                className="ueinput"
                value={district}
                onChange={(e) => setDistrict(e.target.value)}
                data-testid="onb-district-select"
              >
                <option value="">{t("onb.districtPick")}</option>
                {districtOptions.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            ) : (
              <input
                className="ueinput"
                value={district}
                onChange={(e) => setDistrict(e.target.value)}
                placeholder={isTR ? t("onb.districtPh") : t("onb.regionPh")}
                data-testid="onb-district-input"
              />
            )}
          </div>
          <div className="text-xs text-slate-500 mt-1.5">{t("onb.locationHelp")}</div>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          <Field label={t("onb.website")}>
            <input className="ueinput" type="url" value={form.website_url} onChange={(e) => change("website_url", e.target.value)} placeholder="https://..." data-testid="onb-website" />
          </Field>
          <Field label={t("onb.maps")}>
            <input className="ueinput" type="url" value={form.google_maps_url} onChange={(e) => change("google_maps_url", e.target.value)} placeholder="https://maps.google.com/..." data-testid="onb-maps" />
          </Field>
        </div>

        {/* Audience multi-select */}
        <div>
          <label className="text-xs text-slate-400 uppercase tracking-wider block mb-2">{t("onb.audience")}</label>
          <MultiSelect
            options={TARGET_AUDIENCES}
            value={audience}
            onChange={setAudience}
            placeholder={t("onb.audiencePh")}
            testid="onb-audience"
            labelFor={(v) => audienceLabel(v, lang)}
          />
          {audience.includes("Diğer") && (
            <input
              className="ueinput mt-2"
              value={audienceOther}
              onChange={(e) => setAudienceOther(e.target.value)}
              placeholder={t("onb.audienceOtherPh")}
              data-testid="onb-audience-other"
            />
          )}
          <div className="text-xs text-slate-500 mt-1.5">{t("onb.audienceHelp")}</div>
        </div>

        <Field label={t("onb.desc")}>
          <textarea
            className="ueinput min-h-[80px]"
            value={form.description}
            onChange={(e) => change("description", e.target.value)}
            placeholder={t("onb.descPh")}
            data-testid="onb-desc"
          />
        </Field>
        <Field label={t("onb.services")}>
          <textarea
            className="ueinput min-h-[80px]"
            value={form.services}
            onChange={(e) => change("services", e.target.value)}
            placeholder={SERVICES_PLACEHOLDER[preview?.key] || SERVICES_PLACEHOLDER.general}
            data-testid="onb-services"
          />
        </Field>

        <Field label={t("onb.targetQuestions")}>
          <textarea
            className="ueinput min-h-[80px]"
            value={form.target_ai_questions}
            onChange={(e) => change("target_ai_questions", e.target.value)}
            placeholder={t("onb.targetQuestionsPh")}
            data-testid="onb-target-questions"
          />
          <div className="text-xs text-slate-500 mt-1.5">{t("onb.targetQuestionsHelp")}</div>
        </Field>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs text-slate-400 uppercase tracking-wider">{t("onb.competitors")}</label>
            {competitors.length < 3 && (
              <button
                type="button"
                onClick={() => setCompetitors([...competitors, ""])}
                className="text-xs text-cyan-400 hover:text-cyan-300 inline-flex items-center gap-1"
                data-testid="add-competitor"
              >
                <Plus size={12} /> {t("common.add")}
              </button>
            )}
          </div>
          <div className="space-y-2">
            {competitors.map((c, i) => (
              <div key={i} className="flex gap-2">
                <input
                  className="ueinput flex-1"
                  type="url"
                  value={c}
                  onChange={(e) => {
                    const arr = [...competitors];
                    arr[i] = e.target.value;
                    setCompetitors(arr);
                  }}
                  placeholder={t("onb.competitorPh")}
                  data-testid={`onb-competitor-${i}`}
                />
                {competitors.length > 1 && (
                  <button
                    type="button"
                    onClick={() => setCompetitors(competitors.filter((_, j) => j !== i))}
                    className="ghost-btn rounded-lg px-3"
                    aria-label={t("common.delete")}
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-white/5">
          <button
            type="submit"
            disabled={loading}
            className="neon-btn rounded-xl px-7 py-3.5 text-white font-semibold inline-flex items-center gap-2 disabled:opacity-60"
            data-testid="onb-submit"
          >
            {loading && <Loader2 size={16} className="animate-spin" />}
            {t("onb.submit")} <ArrowRight size={16} />
          </button>
        </div>
      </form>

      {preview && (
        <div className="glass-strong rounded-3xl p-7 relative overflow-hidden" data-testid="sector-preview-card">
          <div className="absolute -top-16 -right-16 w-56 h-56 bg-purple-500/10 blur-3xl rounded-full pointer-events-none" />
          <div className="absolute -bottom-16 -left-16 w-56 h-56 bg-cyan-500/10 blur-3xl rounded-full pointer-events-none" />
          <div className="relative">
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-cyan-300 mb-2">
              <Sparkles size={11} /> {preview.label}
            </div>
            <h2 className="font-display text-2xl font-bold text-white mb-1">{t("onb.previewTitle")}</h2>
            <p className="text-sm text-slate-400 mb-5 max-w-2xl">{t("onb.previewDesc")}</p>

            <div className="flex flex-wrap gap-2 mb-7">
              {preview.ai_questions.map((q, i) => (
                <div
                  key={i}
                  className="text-sm text-slate-200 px-3.5 py-2 rounded-full bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-white/10 hover:border-cyan-500/40 transition-colors"
                  data-testid={`preview-q-${i}`}
                >
                  <HelpCircle size={12} className="inline mr-1.5 text-cyan-300 -mt-0.5" />
                  {`"${q}"`}
                </div>
              ))}
            </div>

            <div className="border-t border-white/5 pt-5">
              <div className="text-xs uppercase tracking-wider text-purple-300 mb-3">{t("onb.previewSignals")}</div>
              <div className="grid sm:grid-cols-2 gap-2">
                {preview.key_signals.map((s, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-slate-300" data-testid={`preview-signal-${i}`}>
                    <Check size={14} className="text-emerald-400 shrink-0" />
                    {s}
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-6 flex gap-2 rounded-lg bg-white/[0.02] border border-white/5 p-3">
              <Shield size={14} className="text-slate-500 mt-0.5 shrink-0" />
              <p className="text-xs text-slate-400 leading-relaxed">{t("onb.previewNote")}</p>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .ueinput {
          width: 100%;
          background: rgba(0,0,0,0.4);
          border: 1px solid rgba(255,255,255,0.10);
          border-radius: 0.6rem;
          padding: 0.65rem 0.9rem;
          color: #fff;
          font-size: 0.95rem;
        }
        .ueinput:focus { outline: none; border-color: #3b82f6; box-shadow: 0 0 0 1px #3b82f6; }
        .ueinput::placeholder { color: rgba(148,163,184,0.5); }
        /* Native <option> ignores most CSS, but Chromium + Firefox + Safari
           honour background-color/color on option. Dark theme dropdown. */
        select.ueinput option { background-color: #0f1218; color: #e2e8f0; }
        select.ueinput option:disabled { color: rgba(148,163,184,0.6); }
        select.ueinput option:checked { background-color: #1e293b; color: #fff; }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs text-slate-400 uppercase tracking-wider block mb-2">{label}</label>
      {children}
    </div>
  );
}
